import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, AlertTriangle, Scale, Globe, Lock, FileText, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { cn } from '../lib/utils';

export function TermsPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t, language } = useLanguage();
    
    // Track which sections have been accepted
    const [termsRead, setTermsRead] = useState(false);
    
    // Check if user came from chat to auto-accept terms
    const returnToChat = location.state?.returnToChat;
    const returnPath = location.state?.returnPath || '/chat';

    const handleAcceptTerms = () => {
        // Mark terms as read (first step)
        setTermsRead(true);
        // Scroll to privacy section
        document.getElementById('privacy-section')?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleAcceptPrivacy = () => {
        // Only finalize if terms were read first
        if (!termsRead) {
            // Scroll to terms section first
            document.getElementById('terms-section')?.scrollIntoView({ behavior: 'smooth' });
            return;
        }
        
        // Set terms accepted in localStorage
        localStorage.setItem('iulian_ai_terms_accepted', 'true');
        
        // Navigate back with acceptance flag
        if (returnToChat) {
            navigate(returnPath, { state: { termsAccepted: true } });
        } else {
            navigate('/');
        }
    };

    return (
        <div className="min-h-screen py-8 px-4 md:px-8">
            <div className="max-w-4xl mx-auto">
                {/* Back Button */}
                <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 mb-6 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {language === 'ro' ? 'Înapoi' : 'Back'}
                </button>

                {/* Emergency Disclaimer Banner */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-r from-red-900/30 to-red-800/20 border-2 border-red-500/50 rounded-2xl p-6 mb-8"
                >
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-red-500/30 flex items-center justify-center shrink-0">
                            <AlertTriangle className="w-6 h-6 text-red-400" />
                        </div>
                        <div>
                            <h2 className="text-red-300 font-bold text-lg mb-2">
                                {language === 'ro' 
                                    ? '⚠️ AVERTISMENT URGENȚĂ MEDICALĂ ȘI PSIHOLOGICĂ ⚠️'
                                    : '⚠️ MEDICAL & PSYCHOLOGICAL EMERGENCY DISCLAIMER ⚠️'}
                            </h2>
                            <p className="text-red-200/90 mb-3">
                                {language === 'ro'
                                    ? 'IULIAN AI NU ESTE UN SERVICIU DE CRIZĂ. Dacă ai gânduri suicidare, depresie severă sau orice urgență de sănătate mintală, sună imediat 112 (EU/România) sau 911 (SUA), sau mergi la cea mai apropiată cameră de urgențe.'
                                    : 'IULIAN AI IS NOT A CRISIS SERVICE. If you are experiencing suicidal thoughts, severe depression, or any mental health emergency, immediately call 911 (USA), 112 (EU/Romania), or go to the nearest emergency room.'}
                            </p>
                            <p className="text-red-300/80 text-sm">
                                {language === 'ro'
                                    ? 'Acesta este un instrument de antrenament cognitiv și dezvoltare personală, nu un dispozitiv medical sau un substitut pentru terapie profesională.'
                                    : 'This is a cognitive training and personal development tool, not a medical device or a substitute for professional therapy.'}
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* Terms of Service */}
                <motion.div
                    id="terms-section"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-[#111916] rounded-2xl border border-emerald-800/40 shadow-lg overflow-hidden mb-8"
                >
                    <div className="p-6 border-b border-emerald-800/40 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                            <Scale className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                            <h1 className="font-heading text-2xl font-medium text-emerald-50">
                                {t('termsAndConditions')}
                            </h1>
                            <p className="text-emerald-500 text-sm">
                                {language === 'ro' ? 'Ultima actualizare: 15 Februarie 2026' : 'Last Updated: February 15, 2026'}
                            </p>
                        </div>
                    </div>

                    <div className="p-6 space-y-6 text-emerald-200/90 leading-relaxed">
                        {/* Declaration */}
                        <div className="bg-emerald-900/20 border border-emerald-700/30 rounded-xl p-4">
                            <h3 className="font-heading text-lg text-emerald-100 mb-3 flex items-center gap-2">
                                <Shield className="w-5 h-5 text-emerald-400" />
                                {language === 'ro' ? 'Declarație de Suveranitate Intelectuală' : 'Declaration of Intellectual Sovereignty'}
                            </h3>
                            <p className="text-sm">
                                {language === 'ro'
                                    ? 'Această platformă și toate rezultatele sale generative sunt o manifestare directă a dreptului fundamental al Autorului la Libertatea de Exprimare și Libertatea de Gândire. Iulian AI este o expresie arhitecturală a unei ideologii personale și a unei viziuni umanitare de către autorul Iulian Triboi.'
                                    : 'This platform and all its generative outputs are a direct manifestation of the Author\'s fundamental right to Free Speech and Freedom of Thought. Iulian AI is an architectural expression of a personal ideology and a humanitarian vision by author Iulian Triboi.'}
                            </p>
                        </div>

                        {/* Section 1 */}
                        <div>
                            <h3 className="font-heading text-lg text-emerald-100 mb-2">
                                1. {language === 'ro' ? 'Natura Serviciului & Acuratețe' : 'Nature of Service & Accuracy'}
                            </h3>
                            <p className="mb-2">
                                <strong>1.1</strong> {language === 'ro'
                                    ? 'IULIAN AI este un instrument de dezvoltare personală și antrenament cognitiv bazat pe metodologia proprietară a autorului Iulian Triboi. Este destinat doar scopurilor informative și de auto-reflecție.'
                                    : 'IULIAN AI is an AI-powered personal development and cognitive training tool based on the proprietary methodology of author Iulian Triboi. It is intended for informational and self-reflection purposes only.'}
                            </p>
                            <p className="mb-2">
                                <strong>1.2</strong> {language === 'ro'
                                    ? 'Dată fiind natura probabilistică a învățării automate, utilizarea Serviciilor noastre poate rezulta în Output care nu reflectă cu exactitate persoane, locuri sau fapte reale.'
                                    : 'Given the probabilistic nature of machine learning, use of our Services may result in Output that does not accurately reflect real people, places, or facts.'}
                            </p>
                            <p>
                                <strong>1.3</strong> {language === 'ro'
                                    ? 'Utilizarea acestui Serviciu NU creează o relație doctor-pacient sau terapeut-client. Acesta NU este un substitut pentru consiliere medicală sau psihiatrică profesională.'
                                    : 'Use of this Service does NOT create a doctor-patient or therapist-client relationship. This is NOT a substitute for professional medical or psychiatric advice.'}
                            </p>
                        </div>

                        {/* Section 2 */}
                        <div>
                            <h3 className="font-heading text-lg text-emerald-100 mb-2">
                                2. {language === 'ro' ? 'Eligibilitate & Cerință de Vârstă' : 'Eligibility & Age Requirement'}
                            </h3>
                            <p className="mb-2">
                                <strong>2.1</strong> {language === 'ro'
                                    ? 'Trebuie să ai cel puțin 18 ani pentru a utiliza IULIAN AI. Prin utilizarea acestui Serviciu, declari că ai cel puțin 18 ani.'
                                    : 'You must be at least 18 years of age to use IULIAN AI. By using this Service, you represent and warrant that you are at least 18 years old.'}
                            </p>
                            <p>
                                <strong>2.2</strong> {language === 'ro'
                                    ? 'Dacă ai sub 18 ani, îți este strict interzis să utilizezi acest Serviciu.'
                                    : 'If you are under the age of 18, you are strictly prohibited from using this Service.'}
                            </p>
                        </div>

                        {/* Section 3 */}
                        <div>
                            <h3 className="font-heading text-lg text-emerald-100 mb-2">
                                3. {language === 'ro' ? 'Proprietate Intelectuală' : 'Intellectual Property & Ownership'}
                            </h3>
                            <p>
                                {language === 'ro'
                                    ? 'Serviciul, incluzând toți algoritmii și conținutul subiacent, este proprietatea intelectuală a Autorului, Iulian Triboi. Utilizarea Serviciului îți acordă un drept limitat de acces, nu proprietatea materialelor.'
                                    : 'The Service, including all underlying algorithms and content, is the intellectual property of the Author, Iulian Triboi. Your use of the Service grants you a limited right of access, not ownership of the materials.'}
                            </p>
                        </div>

                        {/* Section 4 */}
                        <div>
                            <h3 className="font-heading text-lg text-emerald-100 mb-2">
                                4. {language === 'ro' ? 'Asumarea Riscurilor' : 'Assumption of Risk & No Reliance'}
                            </h3>
                            <p className="mb-2">
                                <strong>4.1</strong> {language === 'ro'
                                    ? 'Recunoști în mod expres că utilizarea unui sistem AI pentru suport emoțional implică riscuri inerente. Îți asumi voluntar toate riscurile asociate.'
                                    : 'You expressly acknowledge that using an AI system for emotional support involves inherent risks. You voluntarily assume all such risks associated.'}
                            </p>
                            <p>
                                <strong>4.2</strong> {language === 'ro'
                                    ? 'NU trebuie să te bazezi pe Output ca singură sursă de adevăr sau ca bază pentru luarea deciziilor importante de viață, medicale sau legale.'
                                    : 'You must NOT rely on Output as a sole source of truth or as a basis for making important life-altering, medical, or legal decisions.'}
                            </p>
                        </div>

                        {/* Section 5 */}
                        <div>
                            <h3 className="font-heading text-lg text-emerald-100 mb-2">
                                5. {language === 'ro' ? 'Limitarea Răspunderii' : 'Limitation of Liability'}
                            </h3>
                            <p>
                                {language === 'ro'
                                    ? 'În măsura maximă permisă de lege, răspunderea agregată nu va depăși suma pe care ai plătit-o pentru serviciu sau o sută de dolari ($100). Serviciul este furnizat "CA ATARE" fără niciun fel de garanții.'
                                    : 'To the maximum extent permitted by law, aggregate liability shall NOT exceed the greater of the amount you paid for the service or one hundred dollars ($100). The Service is provided on an "AS IS" basis without any warranties.'}
                            </p>
                        </div>

                        {/* Section 6 */}
                        <div>
                            <h3 className="font-heading text-lg text-emerald-100 mb-2">
                                6. {language === 'ro' ? 'Legislație Aplicabilă' : 'Governing Law & Jurisdiction'}
                            </h3>
                            <p>
                                {language === 'ro'
                                    ? 'Acești Termeni sunt guvernați de legile Statului Wyoming, SUA. Orice acțiune legală va fi instituită exclusiv în instanțele de stat sau federale situate în Cheyenne, Wyoming.'
                                    : 'These Terms are governed by the laws of the State of Wyoming, USA. Any legal action shall be instituted exclusively in the state or federal courts located in Cheyenne, Wyoming.'}
                            </p>
                        </div>

                        <p className="text-emerald-500 text-sm text-center pt-4 border-t border-emerald-800/40">
                            © 2026 Iulian AI. All Rights Reserved.
                        </p>
                    </div>

                    {/* Accept Button */}
                    <div className="p-6 border-t border-emerald-800/40 bg-emerald-900/20">
                        <Button
                            onClick={handleAcceptTerms}
                            data-testid="accept-terms-btn"
                            disabled={termsRead}
                            className={cn(
                                "w-full rounded-xl py-6 text-lg font-medium transition-all",
                                termsRead 
                                    ? "bg-emerald-700/50 text-emerald-300 cursor-default"
                                    : "bg-emerald-600 hover:bg-emerald-500 text-white"
                            )}
                        >
                            {termsRead ? (
                                <>
                                    <CheckCircle className="w-5 h-5 mr-2" />
                                    {language === 'ro' ? 'TERMENI ACCEPTAȚI ✓' : 'TERMS ACCEPTED ✓'}
                                </>
                            ) : (
                                <>
                                    <FileText className="w-5 h-5 mr-2" />
                                    {t('readAndAgree')}
                                </>
                            )}
                        </Button>
                    </div>
                </motion.div>

                {/* Privacy Policy */}
                <motion.div
                    id="privacy-section"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-[#111916] rounded-2xl border border-emerald-800/40 shadow-lg overflow-hidden"
                >
                    <div className="p-6 border-b border-emerald-800/40 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                            <Lock className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                            <h1 className="font-heading text-2xl font-medium text-emerald-50">
                                {t('privacyPolicy')}
                            </h1>
                            <p className="text-emerald-500 text-sm">
                                {language === 'ro' ? 'Data intrării în vigoare: 14 Februarie 2026' : 'Effective Date: February 14, 2026'}
                            </p>
                        </div>
                    </div>

                    <div className="p-6 space-y-6 text-emerald-200/90 leading-relaxed">
                        <p className="text-lg">
                            {language === 'ro'
                                ? 'Suntem dedicați respectării confidențialității tale și protejării securității oricăror informații pe care le obținem de la tine. Filozofia noastră este simplă: Gândurile tale sunt ale tale.'
                                : 'We are committed to respecting your privacy and protecting the security of any information we obtain from you. Our philosophy is simple: Your thoughts are your own.'}
                        </p>

                        {/* Health & Wellness Data - Required by Google Play */}
                        <div className="bg-amber-900/20 border border-amber-700/30 rounded-xl p-4 mb-4">
                            <h3 className="font-heading text-lg text-amber-100 mb-3 flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-amber-400" />
                                {language === 'ro' ? 'Date de Sănătate și Wellness' : 'Health & Wellness Data'}
                            </h3>
                            <p className="text-sm mb-3">
                                {language === 'ro'
                                    ? 'IulianAI este o aplicație de wellness care oferă informații educaționale pentru dezvoltare personală și auto-reflecție prin conversații cu AI.'
                                    : 'IulianAI is a wellness companion application that provides educational information for personal development and self-reflection through AI-powered conversations.'}
                            </p>
                            <p className="text-amber-200 text-sm font-medium mb-2">
                                {language === 'ro' ? 'Informații legate de sănătate pe care le poți împărtăși:' : 'Health-related information you may share:'}
                            </p>
                            <ul className="space-y-1 text-sm mb-3">
                                <li>• {language === 'ro' ? 'Stări emoționale, sentimente și preocupări de wellness mental' : 'Emotional states, feelings, and mental wellness concerns'}</li>
                                <li>• {language === 'ro' ? 'Provocări personale, stres, anxietate sau alte subiecte psihologice' : 'Personal challenges, stress, anxiety, or other psychological topics'}</li>
                                <li>• {language === 'ro' ? 'Situații de viață care îți afectează bunăstarea mentală sau emoțională' : 'Life situations that affect your mental or emotional wellbeing'}</li>
                            </ul>
                            <p className="text-emerald-300 text-sm font-medium mb-2">
                                {language === 'ro' ? 'Cum gestionăm aceste date:' : 'How we handle this data:'}
                            </p>
                            <ul className="space-y-1 text-sm">
                                <li>• <strong>{language === 'ro' ? 'NU sunt stocate pe servere:' : 'NOT stored on servers:'}</strong> {language === 'ro' ? 'Conversațiile sunt procesate în timp real și NU sunt salvate' : 'Conversations are processed in real-time and are NOT saved'}</li>
                                <li>• <strong>{language === 'ro' ? 'NU sunt partajate cu terți:' : 'NOT shared with third parties:'}</strong> {language === 'ro' ? 'Nu vindem și nu transferăm datele tale de sănătate' : 'We do not sell or transfer your health data'}</li>
                                <li>• <strong>{language === 'ro' ? 'Stocare locală:' : 'Local storage only:'}</strong> {language === 'ro' ? 'Istoricul chat există doar pe dispozitivul tău' : 'Chat history exists only on your device'}</li>
                                <li>• <strong>{language === 'ro' ? 'Procesare AI:' : 'AI processing:'}</strong> {language === 'ro' ? 'Mesajele sunt trimise la Google Gemini AI pentru generarea răspunsurilor' : 'Messages are sent to Google Gemini AI for generating responses'}</li>
                            </ul>
                        </div>

                        {/* Zero Data Retention */}
                        <div className="bg-emerald-900/20 border border-emerald-700/30 rounded-xl p-4">
                            <h3 className="font-heading text-lg text-emerald-100 mb-3 flex items-center gap-2">
                                <Shield className="w-5 h-5 text-emerald-400" />
                                {language === 'ro' ? 'Principiul Zero Retenție Date' : 'Zero Data Retention Principle'}
                            </h3>
                            <ul className="space-y-2 text-sm">
                                <li>• <strong>{language === 'ro' ? 'Fără Istoric Chat:' : 'No Chat History:'}</strong> {language === 'ro' ? 'Nu stocăm, nu înregistrăm și nu salvăm conversațiile tale.' : 'We do not store, log, or save your conversations.'}</li>
                                <li>• <strong>{language === 'ro' ? 'Procesare Tranzitorie:' : 'Transient Processing:'}</strong> {language === 'ro' ? 'Mesajele tale sunt procesate în memorie volatilă și șterse imediat după generarea răspunsului.' : 'Your prompts are processed in volatile RAM and permanently purged after response generation.'}</li>
                                <li>• <strong>{language === 'ro' ? 'Fără Antrenament pe Date:' : 'No Training on Data:'}</strong> {language === 'ro' ? 'Nu folosim niciodată inputul tău pentru a antrena modelele noastre AI.' : 'We never use your input to train our AI models.'}</li>
                            </ul>
                        </div>

                        {/* What We Don't Collect */}
                        <div>
                            <h3 className="font-heading text-lg text-emerald-100 mb-2">
                                {language === 'ro' ? 'Informații pe care NU le Colectăm' : 'Information We Do NOT Collect'}
                            </h3>
                            <p>
                                {language === 'ro'
                                    ? 'Serviciul nostru este conceput pentru anonimat total. Nu colectăm: identificatori personali, date de cont (nu există înregistrare sau autentificare), sau date financiare.'
                                    : 'Our Service is designed for total anonymity. We do not collect: personal identifiers, account data (no registration or login), or financial data.'}
                            </p>
                        </div>

                        {/* Security */}
                        <div>
                            <h3 className="font-heading text-lg text-emerald-100 mb-2">
                                {language === 'ro' ? 'Securitate' : 'Security'}
                            </h3>
                            <p>
                                {language === 'ro'
                                    ? 'Toate datele în tranzit sunt protejate prin criptare SSL/TLS pe 256 de biți. Deoarece nu există o bază de date cu conversații ale utilizatorilor, nu există un depozit central de gânduri private care ar putea fi compromis.'
                                    : 'All data in transit is protected by 256-bit SSL/TLS encryption. Because there is no database of user conversations, there is no central repository of private thoughts that could be compromised.'}
                            </p>
                        </div>

                        {/* GDPR */}
                        <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-4">
                            <h3 className="font-heading text-lg text-blue-100 mb-2 flex items-center gap-2">
                                <Globe className="w-5 h-5 text-blue-400" />
                                {language === 'ro' ? 'Utilizatori EU & Internaționali (GDPR)' : 'EU & International Users (GDPR)'}
                            </h3>
                            <p className="text-sm text-blue-200/80">
                                {language === 'ro'
                                    ? 'Respectăm standardele globale de confidențialitate. Deoarece nu stocăm date personale sau istoricul chat-ului, "Dreptul de a fi Uitat" și "Dreptul la Acces" sunt exercitate automat prin natura tranzitorie a sistemului nostru.'
                                    : 'We respect global privacy standards. Because we do not store personal data or chat histories, your "Right to be Forgotten" and "Right to Access" are exercised automatically by the transient nature of our system.'}
                            </p>
                        </div>

                        <p className="text-emerald-500 text-sm">
                            {language === 'ro' ? 'Contact: contact@iulianai.com' : 'Contact: contact@iulianai.com'}
                        </p>

                        <p className="text-emerald-500 text-sm text-center pt-4 border-t border-emerald-800/40">
                            © 2026 Iulian AI. All Rights Reserved.
                        </p>
                    </div>

                    {/* Accept Button */}
                    <div className="p-6 border-t border-emerald-800/40 bg-emerald-900/20">
                        {!termsRead && (
                            <p className="text-amber-400 text-sm mb-3 text-center">
                                {language === 'ro' 
                                    ? '⚠️ Mai întâi trebuie să accepți Termenii și Condițiile de mai sus'
                                    : '⚠️ You must first accept the Terms & Conditions above'}
                            </p>
                        )}
                        <Button
                            onClick={handleAcceptPrivacy}
                            data-testid="accept-privacy-btn"
                            disabled={!termsRead}
                            className={cn(
                                "w-full rounded-xl py-6 text-lg font-medium transition-all",
                                termsRead 
                                    ? "bg-emerald-600 hover:bg-emerald-500 text-white"
                                    : "bg-emerald-800/50 text-emerald-500 cursor-not-allowed"
                            )}
                        >
                            <Shield className="w-5 h-5 mr-2" />
                            {t('agreeAndFinalize')}
                        </Button>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}

export default TermsPage;
